import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Label } from "tamagui";

export interface ThemedSelectOption {
  value: string;
  label: string;
}

export interface ThemedSelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
  options: ThemedSelectOption[];
  containerClassName?: string;
  labelClassName?: string;
  selectClassName?: string;
  helperTextClassName?: string;
  initialValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder?: string;
}

const ThemedSelect: React.FC<ThemedSelectProps> = ({
  label,
  helperText,
  error,
  fullWidth = false,
  options,
  containerClassName,
  labelClassName,
  selectClassName,
  helperTextClassName,
  className,
  initialValue,
  onChange,
  placeholder = "Select an option",
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(initialValue || "");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string, optionLabel: string) => {
    setSelectedValue(optionValue);
    setIsOpen(false);
    if (onChange) {
      onChange({
        target: { value: optionValue },
      } as React.ChangeEvent<HTMLSelectElement>);
    }
  };

  const selectedOption = options.find((opt) => opt.value === selectedValue);

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="absolute bg-white border border-terracotta-500 rounded-md shadow-lg max-h-60 overflow-y-auto z-[9999]"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleSelect(option.value, option.label)}
          className="w-full p-2 text-base text-left hover:bg-beige-400 focus:bg-beige-400 focus:outline-none transition-colors"
        >
          {option.label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className="relative">
      {label && <Label htmlFor={props.id}>{label}</Label>}
      {/* Select Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-terracotta-500 rounded-md p-2 text-base bg-white text-left focus:outline-none focus:border-terracotta-600 transition-colors flex justify-between items-center"
      >
        <span className={selectedValue ? "text-gray-900" : "text-gray-500"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Portal the dropdown to document.body */}
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default ThemedSelect;
